// src/services/dropdownService.js
import axios from 'axios';

// Adaptez cette URL selon votre configuration
// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5282/api';
const API_URL = 'https://resolvebo.innovationcode.pro/api';

// Objet pour suivre les modifications par catégorie
const modificationTracker = {
    // Stockage des horodatages des dernières modifications par catégorie
    lastModifications: {},

    // Enregistre une modification pour une catégorie
    recordModification(categoryId) {
        const timestamp = Date.now();
        this.lastModifications[categoryId] = timestamp;
        // Enregistre dans sessionStorage pour persister entre les rechargements de page
        sessionStorage.setItem(`dropdown_modified_${categoryId}`, timestamp.toString());
        return timestamp;
    },

    // Récupère le dernier timestamp de modification
    getLastModification(categoryId) {
        // Vérifier d'abord dans la mémoire
        let timestamp = this.lastModifications[categoryId];

        // Si pas en mémoire, vérifier dans sessionStorage
        if (!timestamp) {
            const storedTimestamp = sessionStorage.getItem(`dropdown_modified_${categoryId}`);
            if (storedTimestamp) {
                timestamp = parseInt(storedTimestamp, 10);
                // Synchroniser avec la mémoire
                this.lastModifications[categoryId] = timestamp;
            }
        }

        return timestamp;
    }
};

const dropdownService = {
    // Stockage des timestamps de cache
    cacheTimestamps: {},

    /**
     * Récupère toutes les catégories de listes déroulantes
     * @returns {Promise<Array>} Liste des catégories avec leurs valeurs
     */
    getAllCategories: async () => {
        try {
            const response = await axios.get(`${API_URL}/dropdown`);
            return response.data;
        } catch (error) {
            console.error("Erreur lors de la récupération des catégories", error);
            throw dropdownService.handleError(error);
        }
    },

    /**
     * Récupère une catégorie spécifique par son ID
     * @param {string} categoryId - L'identifiant de la catégorie
     * @returns {Promise<Object>} La catégorie avec ses valeurs
     */
    getCategoryById: async (categoryId) => {
        try {
            const response = await axios.get(`${API_URL}/dropdown/${categoryId}`);
            return response.data;
        } catch (error) {
            console.error(`Erreur lors de la récupération de la catégorie ${categoryId}`, error);
            throw dropdownService.handleError(error);
        }
    },

    /**
     * Ajoute une nouvelle valeur à une catégorie
     * @param {string} categoryId - L'identifiant de la catégorie
     * @param {Object} valueData - Les données de la valeur à ajouter
     * @returns {Promise<Object>} La valeur créée
     */
    addValue: async (categoryId, valueData) => {
        try {
            // S'assurer que dropdownCategoryId est présent
            const data = {
                ...valueData,
                dropdownCategoryId: categoryId
            };

            // Supprimer l'id s'il est vide (pour la création)
            if (data.id === '') {
                delete data.id;
            }

            const response = await axios.post(`${API_URL}/dropdown/${categoryId}/values`, data);

            // Enregistrer la modification pour cette catégorie
            modificationTracker.recordModification(categoryId);

            // Invalider le cache immédiatement
            dropdownService.invalidateCache(categoryId);

            return response.data;
        } catch (error) {
            console.error(`Erreur lors de l'ajout d'une valeur à la catégorie ${categoryId}`, error);
            throw dropdownService.handleError(error);
        }
    },

    /**
     * Met à jour une valeur existante
     * @param {string} valueId - L'identifiant de la valeur
     * @param {Object} valueData - Les données mises à jour
     * @returns {Promise<Object>} La valeur mise à jour
     */
    updateValue: async (valueId, valueData) => {
        try {
            const response = await axios.put(`${API_URL}/dropdown/values/${valueId}`, valueData);

            // Enregistrer la modification pour cette catégorie
            const categoryId = valueData.dropdownCategoryId;
            if (categoryId) {
                modificationTracker.recordModification(categoryId);
                // Invalider le cache immédiatement
                dropdownService.invalidateCache(categoryId);
            }

            return response.data;
        } catch (error) {
            console.error(`Erreur lors de la mise à jour de la valeur ${valueId}`, error);
            throw dropdownService.handleError(error);
        }
    },

    /**
     * Supprime une valeur
     * @param {string} valueId - L'identifiant de la valeur à supprimer
     * @returns {Promise<boolean>} Succès de l'opération
     */
    deleteValue: async (valueId) => {
        try {
            // Récupérer d'abord les informations de la valeur pour connaître sa catégorie
            const valueData = await axios.get(`${API_URL}/dropdown/values/${valueId}`);
            const categoryId = valueData.data?.dropdownCategoryId;

            await axios.delete(`${API_URL}/dropdown/values/${valueId}`);

            // Enregistrer la modification pour cette catégorie
            if (categoryId) {
                modificationTracker.recordModification(categoryId);
                // Invalider le cache immédiatement
                dropdownService.invalidateCache(categoryId);
            }

            return true;
        } catch (error) {
            console.error(`Erreur lors de la suppression de la valeur ${valueId}`, error);
            throw dropdownService.handleError(error);
        }
    },

    /**
     * Formate les données d'une catégorie pour les utiliser dans un composant Select
     * @param {Object} category - La catégorie obtenue de l'API
     * @returns {Array} Un tableau d'options au format {value, label}
     */
    formatOptionsForSelect: (category) => {
        if (!category || !category.values || !Array.isArray(category.values)) {
            return [];
        }

        return category.values.map(item => ({
            value: item.value,
            label: item.label
        }));
    },

    /**
     * Formate les données d'une catégorie pour les utiliser dans un composant Select avec métadonnées
     * @param {Object} category - La catégorie obtenue de l'API
     * @returns {Array} Un tableau d'options au format {value, label, metadata}
     */
    formatOptionsWithMetadata: (category) => {
        if (!category || !category.values || !Array.isArray(category.values)) {
            return [];
        }

        return category.values.map(item => {
            // Extraire les propriétés standard
            const { id, label, value, dropdownCategoryId, ...metadata } = item;

            // Retourner l'option avec les métadonnées
            return {
                id,
                value,
                label,
                metadata
            };
        });
    },

    /**
     * Récupère directement les options formatées pour un composant Select
     * @param {string} categoryId - L'identifiant de la catégorie
     * @returns {Promise<Array>} Les options formatées pour le Select
     */
    getOptionsForSelect: async (categoryId) => {
        try {
            const category = await dropdownService.getCategoryById(categoryId);
            return dropdownService.formatOptionsForSelect(category);
        } catch (error) {
            console.error(`Erreur lors de la récupération des options pour ${categoryId}`, error);
            return [];
        }
    },

    /**
     * Récupère directement les options avec métadonnées
     * @param {string} categoryId - L'identifiant de la catégorie
     * @returns {Promise<Array>} Les options avec métadonnées
     */
    getOptionsWithMetadata: async (categoryId) => {
        try {
            const category = await dropdownService.getCategoryById(categoryId);
            return dropdownService.formatOptionsWithMetadata(category);
        } catch (error) {
            console.error(`Erreur lors de la récupération des options avec métadonnées pour ${categoryId}`, error);
            return [];
        }
    },

    /**
     * Enregistre l'horodatage du cache
     * @param {string} categoryId - L'identifiant de la catégorie
     * @returns {number} Timestamp du cache
     */
    setCacheTimestamp: (categoryId) => {
        const timestamp = Date.now();
        dropdownService.cacheTimestamps[categoryId] = timestamp;
        // Enregistre également dans le sessionStorage
        sessionStorage.setItem(`dropdown_cache_timestamp_${categoryId}`, timestamp.toString());
        return timestamp;
    },

    /**
     * Récupère l'horodatage du dernier cache
     * @param {string} categoryId - L'identifiant de la catégorie
     * @returns {number|null} Timestamp du cache ou null
     */
    getCacheTimestamp: (categoryId) => {
        // Vérifier d'abord en mémoire
        let timestamp = dropdownService.cacheTimestamps[categoryId];

        // Si pas en mémoire, vérifier dans sessionStorage
        if (!timestamp) {
            const storedTimestamp = sessionStorage.getItem(`dropdown_cache_timestamp_${categoryId}`);
            if (storedTimestamp) {
                timestamp = parseInt(storedTimestamp, 10);
                // Synchroniser avec la mémoire
                dropdownService.cacheTimestamps[categoryId] = timestamp;
            }
        }

        return timestamp;
    },

    /**
     * Cache les options dans le sessionStorage pour des performances améliorées
     * @param {string} categoryId - L'identifiant de la catégorie
     * @param {Array} options - Les options à mettre en cache
     */
    cacheOptions: (categoryId, options) => {
        try {
            const cacheKey = `dropdown_${categoryId}`;
            const cacheData = {
                timestamp: Date.now(),
                options
            };

            // Utiliser sessionStorage au lieu de localStorage
            // Plus approprié pour ce type de données qui n'ont pas besoin de persister longtemps
            sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));

            // Enregistrer l'horodatage du cache séparément
            dropdownService.setCacheTimestamp(categoryId);
        } catch (error) {
            console.error("Erreur lors de la mise en cache des options", error);
        }
    },

    /**
     * Récupère les options du cache s'il existe et n'est pas expiré
     * @param {string} categoryId - L'identifiant de la catégorie
     * @param {number} maxAge - Durée maximale du cache en millisecondes
     * @returns {Array|null} Les options en cache ou null si pas de cache valide
     */
    getCachedOptions: (categoryId, maxAge = 300000) => { // 5 minutes par défaut
        try {
            const cacheKey = `dropdown_${categoryId}`;
            const cachedData = sessionStorage.getItem(cacheKey);

            if (!cachedData) return null;

            const { timestamp, options } = JSON.parse(cachedData);
            const now = Date.now();

            // Vérifier si le cache a expiré
            if (now - timestamp > maxAge) {
                // Cache expiré
                sessionStorage.removeItem(cacheKey);
                return null;
            }

            // Vérifier si des modifications ont été faites après la mise en cache
            const lastModification = modificationTracker.getLastModification(categoryId);
            if (lastModification && lastModification > timestamp) {
                // Les données ont été modifiées après la mise en cache
                sessionStorage.removeItem(cacheKey);
                return null;
            }

            return options;
        } catch (error) {
            console.error("Erreur lors de la récupération du cache", error);
            return null;
        }
    },

    /**
     * Invalide le cache pour une catégorie spécifique
     * @param {string} categoryId - L'identifiant de la catégorie
     */
    invalidateCache: (categoryId) => {
        try {
            const cacheKey = `dropdown_${categoryId}`;
            sessionStorage.removeItem(cacheKey);
            sessionStorage.removeItem(`dropdown_cache_timestamp_${categoryId}`);
            // Supprimer également de la mémoire
            if (dropdownService.cacheTimestamps[categoryId]) {
                delete dropdownService.cacheTimestamps[categoryId];
            }
        } catch (error) {
            console.error("Erreur lors de l'invalidation du cache", error);
        }
    },

    /**
     * Vérifie si le cache doit être rafraîchi en fonction de son âge et des modifications
     * @param {string} categoryId - L'identifiant de la catégorie
     * @param {number} maxAge - Durée maximale du cache en millisecondes
     * @returns {boolean} True si le cache doit être rafraîchi
     */
    shouldRefreshCache: (categoryId, maxAge = 300000) => {
        // Vérifier si des modifications ont été faites
        const lastModification = modificationTracker.getLastModification(categoryId);
        const cacheTimestamp = dropdownService.getCacheTimestamp(categoryId);

        if (lastModification && cacheTimestamp) {
            // Si des modifications ont été faites après la dernière mise en cache
            if (lastModification > cacheTimestamp) {
                return true;
            }
        }

        // Vérifier l'âge du cache
        if (!cacheTimestamp) return true;

        const now = Date.now();
        return (now - cacheTimestamp) > maxAge;
    },

    /**
     * Récupère le dernier timestamp de modification pour une catégorie
     * @param {string} categoryId - L'identifiant de la catégorie
     * @returns {number|null} Timestamp de la dernière modification
     */
    getLastModificationTime: (categoryId) => {
        return modificationTracker.getLastModification(categoryId);
    },

    /**
     * Gère les erreurs de l'API de manière standardisée
     * @param {Error} error - L'erreur survenue
     * @returns {Error} Une erreur formatée avec un message approprié
     */
    handleError: (error) => {
        if (error.response) {
            // Le serveur a répondu avec un code d'erreur
            const message = error.response.data?.message || 'Erreur serveur';
            return new Error(message);
        } else if (error.request) {
            // La requête a été envoyée mais aucune réponse n'a été reçue
            return new Error('Impossible de joindre le serveur. Vérifiez votre connexion.');
        } else {
            // Une erreur s'est produite lors de la configuration de la requête
            return new Error('Une erreur est survenue lors de la préparation de la requête.');
        }
    }
};

export default dropdownService;